/**
 * What the audit record calls things, in one place.
 *
 * <h3>Why this is a module</h3>
 *
 * <p>The Protect audit tab rendered seventeen rows of em-dashes with one column reading
 * {@code LOGGED}. Three of its four columns read field names the endpoint has never returned
 * ({@code occurredAt}, {@code action}, {@code ruleName} against the record's {@code createdAt},
 * {@code eventType}, {@code ruleId}), and the fourth looked the decision up in
 * {@code ALLOW | DENY | TRANSFORM} — a vocabulary {@code PolicyAuditEvent.Decision} does not
 * contain, so every row fell through to the raw enum.</p>
 *
 * <p>One record, one vocabulary. The admin audit page had the right one all along and kept it
 * to itself; it now reads it from here, and {@code __tests__/auditTrailVocabulary.test.js}
 * reads the keys straight out of {@code PolicyAuditEvent.java} so a type or a decision the
 * backend can emit cannot reach a screen unnamed. Ten event types were unnamed when that test
 * was first run, including the two on the screen that prompted it.</p>
 *
 * <h3>What is NOT here</h3>
 *
 * <p>Nothing invents a sentence for an event the record did not describe. Where a field is
 * absent the caller is given null and decides what to draw — a dash in a column that genuinely
 * has nothing is honest; seventeen rows of them because the field was misspelt is not.</p>
 */

export const AUDIT_EVENT_TYPES = [
  // ── Policy + data plane (12) ─────────────────────────────────
  { key: 'POLICY_RULE_EVALUATION', label: 'Policy rule evaluation', group: 'Policy & data' },
  { key: 'SEMANTIC_EVALUATION',    label: 'Semantic evaluation',    group: 'Policy & data' },
  { key: 'DATA_CLASSIFICATION',    label: 'Data classification',    group: 'Policy & data' },
  { key: 'DATA_REDACTION',         label: 'Data redaction',         group: 'Policy & data' },
  { key: 'AGENT_ENFORCEMENT',      label: 'Agent enforcement',      group: 'Policy & data' },
  { key: 'INFERENCE_CALL',         label: 'Inference call',         group: 'Policy & data' },
  { key: 'MCP_TOOL_INVOCATION',    label: 'MCP tool invocation',    group: 'Policy & data' },
  // A governed command refused for its own shape — an argument the action's manifest never
  // declared, or a payload past the customer's configured ceiling. No rule was consulted, because
  // the request did not describe an action the platform offers.
  { key: 'COMMAND_VOCABULARY_REFUSED', label: 'Command refused', group: 'Policy & data' },
  // Both were emitted by the platform and silently rejected by the event_type CHECK until V411, so
  // neither had a filter because neither had ever produced a row.
  { key: 'PII_REVEAL',             label: 'PII reveal',             group: 'Policy & data' },
  { key: 'CONFORMANCE_SCORED',     label: 'Conformance scored',     group: 'Policy & data' },
  { key: 'ANOMALY_DETECTED',       label: 'Anomaly detected',       group: 'Policy & data' },
  { key: 'EMERGENCY_OVERRIDE',     label: 'Emergency override',     group: 'Policy & data' },
  { key: 'TOKEN_CREATED',          label: 'Token created',          group: 'Policy & data' },
  { key: 'TOKEN_RESOLVED',         label: 'Token resolved',         group: 'Policy & data' },
  { key: 'TOKEN_EXPIRED',          label: 'Token expired',          group: 'Policy & data' },
  // ── Cognitive runtime (9) — G262 / AGENT_EXECUTION_MODEL §6 ──
  { key: 'EXECUTION_STARTED',      label: 'Execution started',      group: 'Cognitive runtime' },
  { key: 'COGNITIVE_ITERATION',    label: 'Cognitive iteration',    group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_DECIDED',      label: 'Tool-call decided',      group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_PRE_POLICY',   label: 'Tool-call pre-policy',   group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_EXECUTED',     label: 'Tool-call executed',     group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_POST_POLICY',  label: 'Tool-call post-policy',  group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_RETURNED',     label: 'Tool-call returned',     group: 'Cognitive runtime' },
  { key: 'COGNITIVE_ESCALATION',   label: 'Cognitive escalation',   group: 'Cognitive runtime' },
  { key: 'EXECUTION_COMPLETED',    label: 'Execution completed',    group: 'Cognitive runtime' },
  // ── Identity & sandbox (4) ───────────────────────────────────
  { key: 'SPIFFE_SVID_ISSUED',     label: 'SPIFFE SVID issued',     group: 'Identity & sandbox' },
  { key: 'WASM_SKILL_EXECUTED',    label: 'WASM skill executed',    group: 'Identity & sandbox' },
  { key: 'WASM_SKILL_FAILED',      label: 'WASM skill failed',      group: 'Identity & sandbox' },
  { key: 'WASM_HOST_IMPORT_INVOKED', label: 'WASM host import invoked', group: 'Identity & sandbox' },
  // ── Canvas reconcile (3) ─────────────────────────────────────
  { key: 'CANVAS_RECONCILE_ADDED',   label: 'Canvas reconcile · added',   group: 'Canvas reconcile' },
  { key: 'CANVAS_RECONCILE_REMOVED', label: 'Canvas reconcile · removed', group: 'Canvas reconcile' },
  { key: 'CANVAS_RECONCILE_MUTATED', label: 'Canvas reconcile · mutated', group: 'Canvas reconcile' },
  // ── Framework binding (1) ────────────────────────────────────
  { key: 'FRAMEWORK_BINDING_TRANSITION', label: 'Framework-binding transition', group: 'Framework binding' },
  // ── Intent authorization (1) ─────────────────────────────────
  { key: 'INTENT_AUTHORIZATION',   label: 'Intent authorization',   group: 'Intent authorization' },
  // ── Intent template lifecycle (8) — V212 Phase 1B ────────────
  { key: 'INTENT_TEMPLATE_CREATED',                    label: 'Intent template created',     group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_EDITED',             label: 'Template version edited',     group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_SUBMITTED',          label: 'Template version submitted',  group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_APPROVED',           label: 'Template version approved',   group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_REJECTED',           label: 'Template version rejected',   group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_DEPRECATED',         label: 'Template version deprecated', group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_FLAGGED_CRITICAL',   label: 'Template flagged critical',   group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_ARCHIVED',                   label: 'Intent template archived',    group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_BROKEN_REFS_DETECTED', label: 'Template broken-refs detected', group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_PURGED',                     label: 'Intent template purged',      group: 'Intent template lifecycle' },
  // ── Intent binding lifecycle (8) — V212/V215/V217/V218 ───────
  { key: 'INTENT_BINDING_ATTACHED',           label: 'Binding attached',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_DETACHED',           label: 'Binding detached',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_NARROWED',           label: 'Binding narrowed',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_REBOUND',            label: 'Binding rebound',             group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_DRIFT_DETECTED',     label: 'Binding drift detected',      group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_REACTIVE_NARROWED',  label: 'Binding reactive-narrowed',   group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_MIGRATED',           label: 'Binding migrated',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_FORCE_DETACHED',     label: 'Binding force-detached',      group: 'Intent binding lifecycle' },
  // ── Runtime source flip (1) — V214 Phase 2C ──────────────────
  { key: 'INTENT_RUNTIME_SOURCE_CHANGED',     label: 'Intent runtime-source flipped', group: 'Intent binding lifecycle' },
  // ── Agent lifecycle (12) — V220 Fix 3b ───────────────────────
  // Mirrored from audit_entries via AuditService.logEvent so the
  // hash-chained chain surfaces end-to-end agent activity (create,
  // skill add, eval, phase advance, review, publish, delete) next to
  // policy decisions. See V220__agent_lifecycle_audit_events.sql.
  { key: 'AGENT_CREATED',                     label: 'Agent created',                 group: 'Agent lifecycle' },
  { key: 'AGENT_DELETED',                     label: 'Agent deleted',                 group: 'Agent lifecycle' },
  { key: 'AGENT_PUBLISHED',                   label: 'Agent published',               group: 'Agent lifecycle' },
  { key: 'AGENT_PHASE_ADVANCED',              label: 'Agent phase advanced',          group: 'Agent lifecycle' },
  { key: 'AGENT_SKILL_ADDED',                 label: 'Agent skill added',             group: 'Agent lifecycle' },
  { key: 'AGENT_SKILL_AUTO_CREATED',          label: 'Agent skill auto-created',      group: 'Agent lifecycle' },
  { key: 'AGENT_SKILL_UNPUBLISHED',           label: 'Agent skill unpublished',       group: 'Agent lifecycle' },
  { key: 'SKILL_ADVANCED',                    label: 'Skill phase advanced',          group: 'Agent lifecycle' },
  { key: 'SKILLS_PROMOTED_TO_ACT',            label: 'Skills promoted to Act',        group: 'Agent lifecycle' },
  { key: 'SKILL_REVIEWED',                    label: 'Skill reviewed',                group: 'Agent lifecycle' },
  { key: 'EVAL_SUITE_GENERATED',              label: 'Eval suite generated',          group: 'Agent lifecycle' },
  { key: 'EVAL_RUN_COMPLETED',                label: 'Eval run completed',            group: 'Agent lifecycle' },
  // ── ExteLet registration lifecycle (P7J-RUN-023) ─────────────
  // Mirrored from ExternalAgentRegistration verify/suspend/revoke
  // probe events. Operators auditing ExteLet activity (per P7J
  // AGT-P7J-135) need these chips to filter the chain to just the
  // external-agent lifecycle. Backend emits these via AuditService.
  { key: 'AGENT_REGISTERED',                  label: 'Agent registered (ExteLet)',    group: 'ExteLet registration' },
  { key: 'AGENT_VERIFIED',                    label: 'Agent verified',                group: 'ExteLet registration' },
  { key: 'AGENT_VERIFY_FAILED',               label: 'Agent verify failed',           group: 'ExteLet registration' },
  { key: 'AGENT_SUSPENDED',                   label: 'Agent suspended',               group: 'ExteLet registration' },
  { key: 'AGENT_REVOKED',                     label: 'Agent revoked',                 group: 'ExteLet registration' },
  { key: 'AGENT_PROBE_ISSUED',                label: 'Registration probe issued',     group: 'ExteLet registration' },
  { key: 'SKILL_MAPPING_CHANGED',             label: 'Skill mapping changed',         group: 'ExteLet registration' },
  { key: 'GATEWAY_INVOCATION_SEALED',         label: 'Gateway invocation sealed',     group: 'ExteLet registration' },
  // ── Forms (form_task + external_form) — RC-228 ───────────────
  // Mirrored from ComplianceActivitiesImpl audit emissions. Operators
  // auditing form assignment/submission lifecycle (including Shape A
  // EXTERNAL_SUBJECT round-trips and DYNAMIC expression resolution)
  // filter to this group. Order roughly follows the lifecycle:
  // assignment → status → escalation → reassign → submission, plus the
  // DYNAMIC + EXTERNAL_SUBJECT specialised events.
  { key: 'FORM_TASK_ASSIGNED',                  label: 'Form task assigned',          group: 'Forms' },
  { key: 'FORM_TASK_STATUS_CHANGED',            label: 'Form task status changed',    group: 'Forms' },
  { key: 'FORM_TASK_ESCALATED',                 label: 'Form task escalated',         group: 'Forms' },
  { key: 'FORM_TASK_REASSIGNED_MEMBER_DEPARTED', label: 'Form task reassigned (member departed)', group: 'Forms' },
  { key: 'FORM_TASK_SUBMITTED',                 label: 'Form task submitted',         group: 'Forms' },
  { key: 'FORM_TASK_DYNAMIC_RESOLVED',          label: 'Form task DYNAMIC resolved',  group: 'Forms' },
  { key: 'EXTERNAL_FORM_PAUSE_REGISTERED',      label: 'External form pause registered', group: 'Forms' },
  // ── Added here: ten types the backend emits that no surface could name ───────
  // Found when the Protect audit tab rendered CONTAINER_GUARDRAIL rows as dashes.
  { key: 'INTENT_TEMPLATE_VERSION_CREATED', label: 'Intent version created', group: 'Intent' },
  { key: 'APPROVAL_REQUESTED',      label: 'Approval requested',       group: 'Approvals' },
  { key: 'APPROVAL_ESCALATED',      label: 'Approval escalated',       group: 'Approvals' },
  { key: 'APPROVAL_AWAITING_SECOND', label: 'Awaiting second approver', group: 'Approvals' },
  { key: 'APPROVAL_APPROVED',       label: 'Approval granted',         group: 'Approvals' },
  { key: 'APPROVAL_REJECTED',       label: 'Approval refused',         group: 'Approvals' },
  { key: 'POLICY_BOUND',            label: 'Protection attached',      group: 'Policy & data' },
  { key: 'POLICY_UNBOUND',          label: 'Protection detached',      group: 'Policy & data' },
  { key: 'CONTAINER_GUARDRAIL',     label: 'Container guardrail',      group: 'Runtime' },
  { key: 'CONTAINER_CONTROL',       label: 'Container control',        group: 'Runtime' },
];

/**
 * Every decision {@code PolicyAuditEvent.Decision} defines, and how to draw it.
 *
 * <p>Two tones, because the two surfaces that show these are painted in different palettes:
 * {@code tone} is the v2 console's, {@code p7} is Protect's. One list, so a decision cannot be
 * called one thing on one screen and another on the other — which is the whole reason this
 * module exists.</p>
 */
export const AUDIT_OUTCOMES = [
  { key: 'ALLOWED',   label: 'Allowed',   tone: 'success', p7: 'allow'   },
  { key: 'BLOCKED',   label: 'Blocked',   tone: 'danger',  p7: 'deny'    },
  { key: 'WARN',      label: 'Warn',      tone: 'warning', p7: 'hold'    },
  { key: 'REDACTED',  label: 'Redacted',  tone: 'warning', p7: 'hold'    },
  { key: 'ESCALATED', label: 'Escalated', tone: 'warning', p7: 'hold'    },
  /* Neutral on purpose: a logged event is the record doing its job, not a verdict. */
  { key: 'LOGGED',    label: 'Logged',    tone: 'cyan',    p7: 'neutral' },
  { key: 'FAILED',    label: 'Failed',    tone: 'danger',  p7: 'deny'    },
];

const EVENT_LABEL = Object.fromEntries(AUDIT_EVENT_TYPES.map((e) => [e.key, e.label]));
const OUTCOME = Object.fromEntries(AUDIT_OUTCOMES.map((o) => [o.key, o]));

/** The backend writes `decision`; some payloads carry `outcome`. One normalisation. */
export function outcomeOf(event) {
  return String(event?.outcome || event?.decision || '').toUpperCase() || null;
}

/** The event's own type, uppercased, however the payload spells the field. */
export function eventTypeOf(event) {
  return String(event?.eventType || event?.type || '').toUpperCase() || null;
}

/**
 * When it happened.
 *
 * <p>`createdAt` is what the endpoint returns. The other two are read because other payloads
 * in this codebase carry them, and a timestamp that exists under a different name is not a
 * timestamp that is missing — which is the mistake this module exists to undo.</p>
 */
export function occurredAtOf(event) {
  return event?.createdAt || event?.occurredAt || event?.timestamp || null;
}

/**
 * The outcome as a word, never as the token.
 *
 * <p>An unknown value is sentence-cased rather than printed as it is stored: a decision this
 * build has not heard of is still a decision, and SCREAMING_SNAKE at a tenant is an internal
 * identifier leaking onto a user surface.</p>
 */
export function labelForOutcome(value) {
  const key = String(value || '').toUpperCase();
  if (!key) return null;
  return OUTCOME[key]?.label || sentenceCase(key);
}

/** The Protect tone to draw an outcome in — `allow`, `hold`, `deny`, or `neutral`. */
export function toneForOutcome(value) {
  return OUTCOME[String(value || '').toUpperCase()]?.p7 || 'neutral';
}

/** What the event was, in words. Never the raw type. */
export function labelForEventType(value) {
  const key = String(value || '').toUpperCase();
  if (!key) return null;
  return EVENT_LABEL[key] || sentenceCase(key);
}

/* A type this build has not heard of still has to read as English. */
function sentenceCase(token) {
  const words = token.toLowerCase().split(/[_\s]+/).filter(Boolean);
  if (!words.length) return null;
  return words[0].charAt(0).toUpperCase() + words[0].slice(1) + (words.length > 1 ? ' ' + words.slice(1).join(' ') : '');
}

/**
 * What this event was about: the skill it touched, or what the container reported.
 *
 * <p>Null where the record says nothing, so a caller can leave the column empty rather than
 * print something nobody established.</p>
 */
export function subjectOf(event) {
  if (event?.skillName) return event.skillName;
  if (event?.details?.containerEvent) return String(event.details.containerEvent);
  /* NOT the agent's name. On the agent's own page it is the heading above these rows, so every
     line repeated it and the column that should say what the event was about said who it was
     about, which was already known. */
  return null;
}

/**
 * How many events a page of the record holds.
 *
 * <p>Fifteen: enough that scrolling is rare, few enough that the page below the fold is not
 * where the controls live. The endpoint clamps `size` at 200; the screen asked for 100 and drew
 * all of them in one scroll with nothing saying there were more.</p>
 */
export const AUDIT_PAGE_SIZE = 15;

/**
 * "1–15 of 128", or "9 events" where that is the whole record.
 *
 * <p>Position in the record, in one place, so the two surfaces that show it cannot count
 * differently. Null when there is nothing to count.</p>
 */
export function pageLabel({ number = 0, size = AUDIT_PAGE_SIZE, total = 0 } = {}) {
  if (!total) return null;
  if (total <= size) return `${total} event${total === 1 ? '' : 's'}`;
  const from = number * size + 1;
  const to = Math.min(total, (number + 1) * size);
  return `${from}\u2013${to} of ${total}`;
}

/**
 * When it happened, in the reader's own timezone.
 *
 * <p>The time alone where it was today, the date as well where it was not: this list covers a
 * 720-hour window, and two clock times read as one evening when they can be a month apart. An
 * unparseable or absent timestamp answers null — the caller draws nothing rather than a time
 * nobody recorded.</p>
 *
 * <p>{@code timeZone} is for tests only. A surface passes nothing and gets the reader's own
 * zone, which is the whole point; a test that wants an exact string passes one, because a test
 * asserting a clock face in the machine's local zone passes in UTC and fails in New York —
 * which is how this shipped.</p>
 */
export function timeLabel(event, now = new Date(), timeZone = undefined) {
  const raw = occurredAtOf(event);
  if (!raw) return null;
  const at = new Date(raw);
  if (Number.isNaN(at.getTime())) return null;
  const zone = timeZone ? { timeZone } : {};
  const clock = at.toLocaleTimeString([],
    { hour: '2-digit', minute: '2-digit', hour12: false, ...zone });
  /* Same calendar day IN THE ZONE BEING SHOWN. Comparing getDate() would compare the
     machine's day against a label drawn in another zone. */
  const dayOf = (d) => d.toLocaleDateString('en-CA', zone);
  if (dayOf(at) === dayOf(now)) return clock;
  return `${at.toLocaleDateString([], { day: 'numeric', month: 'short', ...zone })} ${clock}`;
}
